/**
 * ProvidersList Component
 *
 * Displays list of providers with their audit metrics
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyboardArrowDown as ArrowDownIcon,
  PeopleOutline as EmptyIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useProvidersList } from '../../hooks/useProvidersList';

import { ClinicianTableHeader, ClinicianTableRow } from './ClinicianTableRow';
import { PracticeOverview } from './PracticeOverview';

export const ProvidersList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { jotauditDashboard } = useEventTracking();
  const { providers, isLoading, error, refetch, isEmpty } = useProvidersList();

  const [timeRange, setTimeRange] = useState('30days');

  const handleProviderClick = useCallback(
    (providerId: string, providerName?: string) => {
      // Track provider click
      jotauditDashboard.providerClicked({
        provider_id: providerId,
        provider_name: providerName,
      });
      // Navigate to provider detail view
      navigate(`/jotaudit/provider/${providerId}`);
    },
    [navigate, jotauditDashboard],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading providers</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F8F9FB', p: 3, borderRadius: 4, mt: -3, mx: -3 }}>
      {/* Top Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          size="small"
          IconComponent={ArrowDownIcon}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '& .MuiSelect-select': {
              py: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              textTransform: 'uppercase',
            },
          }}
        >
          <MenuItem value="7days">Past 7 Days</MenuItem>
          <MenuItem value="30days">Past 30 Days</MenuItem>
          <MenuItem value="90days">Past 90 Days</MenuItem>
        </Select>

        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          sx={{
            bgcolor: '#2D336B',
            textTransform: 'uppercase',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            '&:hover': {
              bgcolor: '#1E2248',
            },
          }}
        >
          Invite User
        </Button>
      </Box>

      {/* Practice Overview */}
      <PracticeOverview />

      {/* Clinicians Table */}
      <Box>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <ClinicianTableHeader />

          {/* Loading State */}
          {isLoading && providers.length === 0 ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Skeleton variant="circular" width={44} height={44} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton width="20%" height={16} />
                  </Box>
                  <Skeleton width={60} height={20} />
                  <Skeleton width={60} height={20} />
                  <Skeleton width={80} height={24} />
                </Box>
              ))}
            </Box>
          ) : isEmpty ? (
            /* Empty State */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: theme.palette.grey[100],
                  mb: 2,
                }}
              >
                <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
              </Box>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                No providers found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
              >
                Audit data will appear here once notes are audited
              </Typography>
            </Box>
          ) : (
            /* Provider List */
            <Box sx={{ position: 'relative' }}>
              {isLoading && providers.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: alpha(theme.palette.background.paper, 0.7),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {providers.map(provider => (
                <ClinicianTableRow
                  key={provider.user_id}
                  provider={provider}
                  onClick={handleProviderClick}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ProvidersList;
